<template>
    <div class="profile-media">
        <v-alert
            v-if="error"
            :value="true"
            type="error"
            class="notranslate"
        >{{ $t('There is an error with your media submission') }}<br/>{{error}}</v-alert>
        <div class="profile-media__upload">
            <span class="limit">{{ available > 0
                ? $t('You can send up to more', { available })
                : $t('You have reached the image limit')
            }}</span>
            <input type="file"
                   :multiple="(available > 1)"
                   ref="image_uploader"
                   value="upload dos arquivos"
                   @change="upload"
            />
            <v-btn color="accent"
                   @click.native="onFocus"
                   :loading="loading"
                   :disabled="loading || (available < 1)"
            >
                {{ $t('Submit pictures') }}
                <v-icon class="notranslate" right dark>cloud_upload</v-icon>
            </v-btn>
            <span class="note">{{ $t('Images requirements') }}</span>
            <span class="tutorial_link">
                <a href="https://youtu.be/fRDthHhqyXk" target="_blank">{{ $t('Watch the guide here') }}</a>
            </span>
        </div>
        <div class="profile-media__gallery">
            <ul v-if="Object.keys(images).length > 0">
                <li v-for="image in images"
                    :key="image"
                    :style="`background-image: url(${initialImages[image]});`"
                    :data-removing-text="$t('Removing')"
                    :class="
                        removing.includes(image) ? 'removing' : '' +
                        featuredImage === image ? ' featured' : ''
                    "
                >
                    <div class="actions">
                        <v-tooltip bottom>
                            <v-btn slot="activator"
                                   flat
                                   icon
                                   color="#fff"
                                   @click="removeImage(image)"
                            >
                                <v-icon class="notranslate" medium>delete</v-icon>
                            </v-btn>
                            <span>{{ $t('Remove picture') }}</span>
                        </v-tooltip>
                    </div>
                </li>
            </ul>
        </div>
        <v-snackbar
            v-model="snackbar"
            :bottom="true"
            :vertical="true"
            :timeout="3000"
        >
            {{ $t('Images uploaded successfully') }}
        </v-snackbar>
    </div>
</template>

<script>
import VSnackbar from 'vuetify/es5/components/VSnackbar/VSnackbar'
import VAlert from 'vuetify/es5/components/VAlert/VAlert'
import * as api from '../utils/api'
import { UPDATE_SEGMENTS } from '../store/types'
import { validateImage } from '../utils/functions'
export default {
  name: 'ProfileImages',
  components: { VSnackbar, VAlert },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  data: () => ({
    loading: false,
    error: false,
    snackbar: false,
    featuredImage: 0,
    removing: [],
    after_submit_alert_shown: false
  }),
  computed: {
    limit: function () {
      return parseInt(this.$store.state.segments.find(el => {
        return el.data.id === this.id
      }).data['media_limit'], 10)
    },
    initialImages: function () {
      return this.$store.state.segments.find(el => {
        return el.data.id === this.id
      }).data.media
    },
    images: function () {
      return Object.keys(this.initialImages).reverse().map(key => {
        return key
      })
    },
    available: function () {
      return (this.limit - this.images.length)
    }
  },
  mounted () {
    this.featuredImage = this.$store.state.segments.find(el => {
      return el.data.id === this.id
    }).data.featured_image
  },
  methods: {
    onFocus: function () {
      this.$refs.image_uploader.click()
    },
    upload: function () {
      this.loading = true
      this.error = false
      if (this.$refs.image_uploader.files.length <= this.available) {
        const images = Array.prototype.map.call(
          this.$refs.image_uploader.files,
          image => validateImage(image, 1200, 800).then(data => data)
        )
        Promise.all(images).then(base64Imgs => {
          api.uploadImages(this.id, base64Imgs).then(result => {
            this.loading = false
            this.snackbar = true
            this.$refs.image_uploader.value = ''
            this.$store.commit(UPDATE_SEGMENTS, result)
            if (
              (this.images.length >= 3 && !this.after_submit_alert_shown) ||
              this.images.length >= this.limit
            ) {
                this.$emit('after-submit-alerts');
                this.after_submit_alert_shown = true
            }
          })
        }).catch(error => {
          this.error = this.$t('File does not meet requirements', {file: error.message})
          this.loading = false
        })
      } else {
        this.error = this.$t('Image upload limit is', {limit: this.available})
        this.loading = false
      }
    },
    removeImage: function (id) {
      this.removing.push(id)
      api.removeImage(this.id, id).then(result => {
        const index = this.removing.indexOf(id)
        if (index !== -1) this.removing.splice(index, 1)
        this.$store.commit(UPDATE_SEGMENTS, result)
      })
    },
    pickFeaturedImage: function (id) {
      this.featuredImage = (this.featuredImage === id) ? 0 : id
      api.pickFeaturedImage(this.id, this.featuredImage)
    }
  }
}
</script>

<style lang="scss">
    @keyframes removingAnimation{
        0%{
            background-position: -200px 0;
        }
        100% {
            background-position: 200px 0
        }
    }
    .profile-media {
        .profile-media__upload {
            background: #f5f5f5;
            border-radius: 8px;
            max-width: 800px;
            margin: 24px auto;
            padding: 36px;
            text-align: center;
            input[type=file] {
                display: none;
            }
            .limit {
                display: block;
                font-size: 16px;
            }
            .note {
                display: block;
                margin-top: 12px;
            }
            .tutorial_link {
                display: inline-block;
                margin: 4px 0;
                background: var(--warning-color);
                padding: 4px 6px;
                line-height: 24px;
                a {
                    color: #fff;
                    text-decoration: none;
                }
            }
        }
        .profile-media__gallery {
            ul {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                grid-gap: 4px;
                margin: 0;
                padding: 0;
                list-style: none;
                li {
                    display: block;
                    position: relative;
                    background-color: #fff;
                    padding-top: 100%;
                    background-position: center;
                    background-size: contain;
                    &:hover .actions {
                        opacity: 1;
                    }
                    .actions {
                        position: absolute;
                        top: 0;
                        right: 0;
                        bottom: 0;
                        left: 0;
                        background: rgba(0,0,0,0.5);
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        opacity: 0;
                        transition: opacity 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
                    }
                    &.removing {
                        pointer-events: none;
                        opacity: 1;
                    }
                    &.removing::before {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        font-size: 24px;
                        color: rgba(0,0,0,0.87);
                        line-height: 100%;
                        content: attr(data-removing-text);
                        position: absolute;
                        top: 0;
                        right: 0;
                        bottom: 0;
                        left: 0;
                        animation-duration: 1s;
                        animation-fill-mode: forwards;
                        animation-iteration-count: infinite;
                        animation-name: removingAnimation;
                        animation-timing-function: linear;
                        background: #f6f7f8;
                        background: linear-gradient(
                            to right,
                            #eeeeee 8%,
                            #dddddd 18%,
                            #eeeeee 33%
                        );
                        background-size: 100% 100%;
                        opacity: 0.6
                    }
                    &.featured::after {
                        content: 'star';
                        font-family: 'Material Icons';
                        font-weight: normal;
                        font-style: normal;
                        letter-spacing: normal;
                        text-transform: none;
                        display: inline-block;
                        white-space: nowrap;
                        word-wrap: normal;
                        direction: ltr;
                        -webkit-font-feature-settings: 'liga';
                        -webkit-font-smoothing: antialiased;
                        position: absolute;
                        top: 0;
                        right: 0;
                        font-size: 36px;
                        text-align: right;
                        padding: 8px;
                        line-height: 36px;
                        color: #fff;
                        text-shadow: 0 3px 24px rgba(0,0,0,0.5);
                    }
                }
            }
        }
    }
</style>

<i18n>
    {
        "en": {
            "There is an error with your media submission": "There is an error with your media submission",
            "You can send up to more": "You can send up to {available} pictures",
            "You have reached the image limit": "You have reached the image limit",
            "Submit pictures": "Submit pictures",
            "Watch the guide here": "Need help to resize the pictures? Watch the guide here",
            "Images requirements": "Images must be .jpg and 1200x800 pixels.",
            "Removing": "Removing...",
            "Remove picture": "Remove picture",
            "Images uploaded successfully": "Images uploaded successfully!",
            "File does not meet requirements": "File {file} does not meet the requirements",
            "Image upload limit is": "Image upload limit is {limit} files"
        },
        "pt": {
            "There is an error with your media submission": "Houve um problema com o seu envio de imagens",
            "You can send up to more": "Você pode enviar até mais {available} imagens",
            "You have reached the image limit": "Você atingiu o limite de imagens",
            "Submit pictures": "Enviar imagens",
            "Watch the guide here": "Dúvidas sobre como redimensionar? Assista o tutorial aqui",
            "Images requirements": "Imagens devem ser na extenção .jpg e nas dimensões 1200x800 pixels.",
            "Removing": "Removendo...",
            "Remove picture": "Remover imagem",
            "Images uploaded successfully": "Imagens enviadas com sucesso!",
            "File does not meet requirements": "Arquivo {file} fora dos requerimentos",
            "Image upload limit is": "Limite de envio de imagens é de {limit} arquivos"
        }
    }
</i18n>
